import { useState } from "react";
import { MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { useSalveazaTargetareScor } from "@/hooks/api/mutate/useSalveazaTargetareScor";
import { useToast } from "@/hooks/use-toast";
import type { TargetareScenariuScor } from "@/types/targetare";
import {
  ETICHETE_POTRIVIRE_CAEN,
  ETICHETE_POTRIVIRE_LISTA,
} from "@/utils/targetareConfig";
import { formatNumar } from "@/utils/targetareFormatting";
import type { FirmeCriteriuDeschis } from "../FirmeCriteriuDialog";

type ScenariiScorProps = {
  scenarii: TargetareScenariuScor[];
  firmePeScor: Map<number, { n: number; n_scop: number }>;
  onFirme: (deschis: FirmeCriteriuDeschis) => void;
};

function CampScor({
  scenariu,
  onSalveaza,
  inCurs,
}: {
  scenariu: TargetareScenariuScor;
  onSalveaza: (scor: number) => void;
  inCurs: boolean;
}) {
  const [valoare, setValoare] = useState(String(scenariu.scor));
  const confirma = () => {
    const n = Number(valoare);
    if (!Number.isInteger(n) || n < 0 || n > 100) {
      setValoare(String(scenariu.scor));
      return;
    }
    if (n !== scenariu.scor) onSalveaza(n);
  };
  return (
    <Input
      type="number"
      min={0}
      max={100}
      value={valoare}
      disabled={inCurs}
      onChange={(e) => setValoare(e.target.value)}
      onBlur={confirma}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
      className="h-8 w-[84px] text-right font-mono text-sm tabular-nums"
    />
  );
}

function Descriere({
  scenariu,
  onSalveaza,
}: {
  scenariu: TargetareScenariuScor;
  onSalveaza: (descriere: string) => void;
}) {
  const [text, setText] = useState(scenariu.scor_descriere ?? "");
  return (
    <Popover onOpenChange={(v) => v && setText(scenariu.scor_descriere ?? "")}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex max-w-[420px] items-center gap-1 truncate rounded px-1 text-left text-xs text-muted-foreground hover:bg-muted"
          title={
            scenariu.scor_descriere ?? "Adaugă descrierea afișată în formular"
          }
        >
          <MessageSquare className="h-3 w-3 shrink-0" />
          <span className="truncate">
            {scenariu.scor_descriere ?? "descriere…"}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[480px] space-y-2">
        <p className="text-xs text-muted-foreground">
          Textul afișat utilizatorului în formularul de notificare când
          serviciul este propus cu acest scenariu.
        </p>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={5}
        />
        <Button size="sm" className="w-full" onClick={() => onSalveaza(text)}>
          Salvează descrierea
        </Button>
      </PopoverContent>
    </Popover>
  );
}

export function ScenariiScor({
  scenarii,
  firmePeScor,
  onFirme,
}: ScenariiScorProps) {
  const { toast } = useToast();
  const salveaza = useSalveazaTargetareScor();
  const modifica = (
    s: TargetareScenariuScor,
    patch: { scor?: number; descriere?: string }
  ) =>
    salveaza.mutate(
      {
        id: s.id,
        scor: patch.scor ?? s.scor,
        descriere: patch.descriere ?? null,
      },
      {
        onSuccess: () =>
          toast({
            title: "Scenariu salvat",
            description: `${ETICHETE_POTRIVIRE_CAEN[s.potrivire_caen] ?? s.potrivire_caen} + ${ETICHETE_POTRIVIRE_LISTA[s.potrivire_lista] ?? s.potrivire_lista}. Scorul firmelor se recalculează la următoarea reîncadrare.`,
          }),
        onError: (e) =>
          toast({
            title: "Salvarea a eșuat",
            description: e.message,
            variant: "destructive",
          }),
      }
    );
  const ordonate = [...scenarii].sort(
    (a, b) =>
      b.scor - a.scor || a.potrivire_caen.localeCompare(b.potrivire_caen)
  );

  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        <thead className="bg-muted/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="px-3 py-2 font-medium">Potrivire CAEN</th>
            <th className="px-3 py-2 font-medium">Listă</th>
            <th className="px-3 py-2 text-right font-medium">Scor</th>
            <th className="px-3 py-2 text-right font-medium">
              Firme cu scorul (în scop / total)
            </th>
            <th className="px-3 py-2 font-medium">Descriere în formular</th>
          </tr>
        </thead>
        <tbody>
          {ordonate.map((s) => {
            const f = firmePeScor.get(s.scor);
            return (
              <tr key={s.id} className="border-t">
                <td className="px-3 py-1.5">
                  {ETICHETE_POTRIVIRE_CAEN[s.potrivire_caen] ??
                    s.potrivire_caen}
                  <span className="block font-mono text-[11px] text-muted-foreground">
                    {s.potrivire_caen || "(fără)"}
                  </span>
                </td>
                <td className="px-3 py-1.5">
                  {ETICHETE_POTRIVIRE_LISTA[s.potrivire_lista] ??
                    s.potrivire_lista}
                </td>
                <td className="px-3 py-1.5 text-right">
                  <CampScor
                    scenariu={s}
                    inCurs={salveaza.isPending}
                    onSalveaza={(scor) => modifica(s, { scor })}
                  />
                </td>
                <td className="px-3 py-1.5 text-right">
                  <button
                    type="button"
                    className="tabular-nums hover:underline disabled:no-underline disabled:opacity-50"
                    disabled={!f}
                    onClick={() =>
                      onFirme({
                        criteriu: { tip: "scor", min: s.scor, max: s.scor },
                        titlu: `Firme cu scorul de potrivire ${s.scor}`,
                      })
                    }
                  >
                    {f ? `${formatNumar(f.n_scop)} / ${formatNumar(f.n)}` : "–"}
                  </button>
                </td>
                <td className="px-3 py-1.5">
                  <Descriere
                    scenariu={s}
                    onSalveaza={(d) => modifica(s, { descriere: d })}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
